<script setup lang="ts">
/**
 * SectionChecklist
 *
 * Two or three column layout with large icons and bold headings.
 * Designed for scannability - "Why Us", "How We Work", etc.
 *
 * This is a TRUST section, so uses green (not mw-accent):
 * - Green icons signal verification/trust
 * - Icon container scales on hover
 * - No lift (static content, not clickable)
 *
 * Supports two content formats:
 * - Standard: { icon, heading, description }
 * - Legacy/Projects: { title, description } (no icon)
 */

import type { SectionChecklistProps } from "~~/shared/types";

interface LegacyItem {
  title?: string;
  description?: string;
}

type FlexibleItem = SectionChecklistProps["items"][number] | LegacyItem;

interface FlexibleProps extends Omit<SectionChecklistProps, "items"> {
  items: FlexibleItem[];
}

const props = withDefaults(defineProps<FlexibleProps>(), {
  columns: 2,
  sectionBg: "white",
});

/**
 * Normalize items to handle both standard and legacy formats
 */
function getItemHeading(item: FlexibleItem): string {
  return (item as { heading?: string }).heading || (item as LegacyItem).title || "";
}

function getItemDescription(item: FlexibleItem): string {
  return item.description || "";
}

function getItemIcon(item: FlexibleItem): string | undefined {
  return (item as { icon?: string }).icon;
}

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

const gridColsClass = computed(() =>
  props.columns === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"
);
</script>

<template>
  <section :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Header -->
      <div class="mb-12 text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {{ title }}
        </h2>
        <p v-if="subtitle" class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600">
          {{ subtitle }}
        </p>
      </div>

      <!-- Checklist Grid -->
      <div :class="['grid gap-x-12 gap-y-10', gridColsClass]">
        <div v-for="(item, index) in items" :key="index" class="group flex gap-5">
          <!-- Icon Container - Green for trust/verification (only show if icon provided) -->
          <div v-if="getItemIcon(item)" class="shrink-0">
            <div
              class="flex size-14 items-center justify-center rounded-xl bg-primary-50 transition-all duration-300 group-hover:scale-110 group-hover:bg-primary-100"
            >
              <Icon :name="getItemIcon(item)!" size="32" class="text-primary-500" />
            </div>
          </div>
          <!-- Fallback check icon for items without custom icons -->
          <div v-else class="shrink-0">
            <div
              class="flex size-14 items-center justify-center rounded-xl bg-primary-50 transition-all duration-300 group-hover:scale-110 group-hover:bg-primary-100"
            >
              <Icon name="lucide:check" size="32" class="text-primary-500" />
            </div>
          </div>

          <!-- Content -->
          <div>
            <h3 class="mb-2 text-lg font-bold text-slate-900">
              {{ getItemHeading(item) }}
            </h3>
            <p class="leading-relaxed text-neutral-600">
              {{ getItemDescription(item) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
